import { publicClientFetcher } from "./client-fetcher";
import type { TrainingSessionData, TrainingParams } from "@workspace/types";
import type { PublicDemoRecap } from "@/lib/types/demo-session";

type PublicTrainingSessionParams = TrainingParams & {
  demoLinkSlug?: string;
  previewToken?: string;
};

/**
 * Create a new public training session
 */
export const createPublicTrainingSession = async (
  params: PublicTrainingSessionParams,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.post("/public/training-sessions", {
      training_scenario_type: params.scenarioType,
      ...(params.demoLinkSlug ? { demo_link_slug: params.demoLinkSlug } : {}),
      ...(params.previewToken ? { preview_token: params.previewToken } : {}),
    })
  ).data;
};

/** Also works mid-session, for reconnect hydration. */
export const getPublicDemoRecap = async (
  sessionId: string,
): Promise<PublicDemoRecap> => {
  return (
    await publicClientFetcher.get(
      `/public/training-sessions/${sessionId}/recap`,
    )
  ).data;
};

/**
 * Get training session details (for polling feedback)
 */
export const getPublicTrainingSession = async (
  sessionId: string,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.get(`/public/training-sessions/${sessionId}`)
  ).data;
};

/**
 * Mark training session as complete
 */
export const completePublicTrainingSession = async (
  sessionId: string,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.post(
      `/public/training-sessions/${sessionId}/complete`,
    )
  ).data;
};

/** Rejects with session_already_paused / session_not_pausable. */
export const pausePublicTrainingSession = async (
  sessionId: string,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.post(
      `/public/training-sessions/${sessionId}/pause`,
    )
  ).data;
};

/** Idempotent — resuming an already-live call just returns its current state. */
export const resumePublicTrainingSession = async (
  sessionId: string,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.post(
      `/public/training-sessions/${sessionId}/resume`,
    )
  ).data;
};

export const endCoachFreeze = async (
  sessionId: string,
): Promise<TrainingSessionData> => {
  return (
    await publicClientFetcher.post(
      `/public/training-sessions/${sessionId}/freeze/end`,
    )
  ).data;
};
